'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { type ReactNode, type RefObject, useRef } from 'react';
import type PageType from '@alltrails/amplitude/enums/PageType';
import Heading from '@alltrails/denali/components/Heading';
import useLogWebSectionViewed from '@alltrails/layout/hooks/useLogWebSectionViewed';
import { selectCustomRouteElevationData } from '@alltrails/maps/redux/customRoute';
import { useAppSelector } from '@/utils/redux';
import styles from './SidebarElevationChart.module.scss';

const ElevationChart = dynamic(() => import('@alltrails/maps/components/ElevationChart/ElevationChart'), { ssr: false });

// Mounted only when a pageType is supplied so the IntersectionObserver inside useLogWebSectionViewed isn't spun up on untracked sidebars.
const SectionViewedTracker = ({ targetRef, pageType }: { targetRef: RefObject<HTMLDivElement | null>; pageType: PageType }) => {
  useLogWebSectionViewed(targetRef, 'Elevation Graph', pageType);
  return null;
};

type SidebarElevationChartProps = {
  activeSegmentIndex?: number;
  // When provided, fires the 'Elevation Graph' section-viewed amplitude event for the given page.
  pageType?: PageType;
  // Composed with the default container styling so call sites can override height/margin.
  className?: string;
  // When provided, renders a section heading above the chart.
  heading?: ReactNode;
  // When true, render the empty-state chart (axes only).
  renderWhenEmpty?: boolean;
};

const SidebarElevationChart = ({ activeSegmentIndex, pageType, className, heading, renderWhenEmpty }: SidebarElevationChartProps) => {
  const elevationData = useAppSelector(selectCustomRouteElevationData);
  const ref = useRef<HTMLDivElement>(null);

  const hasData = elevationData.some(segment => segment.length > 0);

  if (!hasData && !renderWhenEmpty) {
    return null;
  }

  const chart = (
    <div ref={ref} className={classNames(styles.container, className)}>
      {pageType && <SectionViewedTracker targetRef={ref} pageType={pageType} />}
      <ElevationChart elevationData={elevationData} activeSegmentIndex={activeSegmentIndex} />
    </div>
  );

  if (!heading) {
    return chart;
  }

  return (
    <>
      <Heading as="h2" size="200">
        {heading}
      </Heading>
      {chart}
    </>
  );
};

export default SidebarElevationChart;
